import { GuestPreferenceStatModel } from 'api-hooks/guest';
import { PreferenceGroupSubTypeEnum } from 'common/constants/enum';
import { capitalize } from 'common/utils/string';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import { ActionType, ColumnDataType } from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useGetMultiplePreferenceColumn from 'hooks/use-get-multiple-preference-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewGuestItem } from '../../hooks/use-get-default-values';
import {
  TicketSaleFlightGuestFormType,
  TicketSaleFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function tagTransformer(item: GuestPreferenceStatModel) {
  return {
    id: item.id,
    label: `${item.name} ${item.count > 1 ? `(X${item.count})` : ''}`,
  };
}

function useScheduleGuestTableList() {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();
  const multiplePreferenceColumn = useGetMultiplePreferenceColumn({
    subType: PreferenceGroupSubTypeEnum.Flight,
  });
  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    {
      header: t('common:ticket'),
      accessorKey: 'ticket',
      minSize: 100,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:guest_preferences'),
      accessorKey: 'guestPreferences',
      minSize: 400,
      dataType: ColumnDataType.tags,
      tagKeyName: 'label',
      maxTags: 2,
    },
    {
      header: t('guest:booking_preferences'),
      accessorKey: 'preferences',
      minSize: 250,
      dataType: ColumnDataType.selectMulti,
      selectProps: multiplePreferenceColumn,
    },
    ...deleteColumn(editable),
  ];
}

export default function ScheduleGuestTable(props: Props) {
  const { editable } = useFormState();
  const name = `flightSales.${props.parentIndex}.flightSaleGuests` as const;

  const { clearErrors, trigger, getValues, control } =
    useFormContext<TicketSaleFormType>();

  const fields = getValues(name);
  const { update, remove, append } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useScheduleGuestTableList();

  const dispatch = React.useCallback(
    (action) => {
      const currentRowIndex = action.rowIndex as number;
      const type = action.type as ActionType;
      const guests = getValues(name);
      const guest = guests[currentRowIndex];
      const columnId = action.columnId as keyof TicketSaleFlightGuestFormType;
      const value = action.value;

      const otherVal: () => Partial<TicketSaleFlightGuestFormType> = () => {
        if (columnId === 'guestId') {
          const allPreference = action.extra?.[0]
            ? [
                ...action.extra[0].extra.preferences,
                ...action.extra[0].extra.preferenceStats,
              ]
            : [];

          return {
            guestPreferences: (allPreference || []).map((item) =>
              tagTransformer(item),
            ),
            guestName: action.extra?.[0]
              ? `${capitalize(action.extra?.[0].extra.title)} ${
                  action.extra[0].extra.name
                }`
              : '',
          };
        }

        return {};
      };

      if (type === 'addRow') {
        append(addNewGuestItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
